/**
 * FASE 23.1 — RENDER PRIORITY RESOLVER
 *
 * Decides which proactive UI elements should render and which should be
 * suppressed. Prevents cognitive overload in the Kevia surface.
 *
 * Rules (in priority order):
 *  - max 1 banner visible
 *  - max 2 proactive hints (insights)
 *  - max 3 action chips
 *  - never show 2 urgent signals simultaneously with redundant text
 *  - never show followup panel when empty
 *  - never surface "info" banner if no meaningful action follows
 *  - session cooldown: same hint suppressed for 3 minutes
 */

/* ─── Session-scoped suppression state ──────────────────────────── */

/** @type {Map<string, number>} hintKey → last-shown timestamp */
const _hintCooldowns  = new Map();
/** last banner level shown and when */
let _lastBannerLevel  = null;
let _lastBannerTs     = 0;

const HINT_COOLDOWN_MS   = 3 * 60 * 1000;  // 3 min
const INFO_BANNER_MAX_MS = 5 * 60 * 1000;  // info banner disappears after 5 min quiet time

/* ─── Helpers ────────────────────────────────────────────────────── */

function isCooledDown(key) {
  const last = _hintCooldowns.get(key);
  return last === undefined || Date.now() - last >= HINT_COOLDOWN_MS;
}

function markSeen(key) {
  _hintCooldowns.set(key, Date.now());
}

/* ─── Public API ─────────────────────────────────────────────────── */

/**
 * @typedef {Object} RenderDecision
 * @property {object|null}  banner          — banner to render, or null
 * @property {object[]}     chips           — max 3 action chips
 * @property {string|null}  insightText     — max 1 insight text
 * @property {boolean}      shouldShowFollowup
 * @property {string[]}     suppressed      — reason codes for dev observability
 */

/**
 * Resolve which UI elements should render given the raw proactive hints.
 * Stateful: updates session-level cooldowns on each call.
 *
 * @param {{
 *   priorityBanner: object|null,
 *   nextActionItems: object[],
 *   insightText: string|null,
 *   followupItems: object[],
 *   overdueCount?: number,
 * }} hints
 * @returns {RenderDecision}
 */
export function resolveRenderPriority({
  priorityBanner  = null,
  nextActionItems = [],
  insightText     = null,
  followupItems   = [],
  overdueCount    = 0,
} = {}) {
  const suppressed = [];
  const now        = Date.now();

  /* ── Banner suppression ─────────────────────────────────────── */
  let banner = null;

  if (priorityBanner) {
    const level = priorityBanner.level;
    const bannerKey = `banner:${level}`;

    if (level === "info") {
      // Info banners: suppress if no overdue workflows and recently shown
      if (overdueCount === 0 && !isCooledDown(bannerKey)) {
        suppressed.push("info-banner-cooldown");
      } else if (overdueCount === 0 && now - _lastBannerTs < INFO_BANNER_MAX_MS && _lastBannerLevel === "info") {
        suppressed.push("info-banner-quiet");
      } else {
        banner = priorityBanner;
        markSeen(bannerKey);
        _lastBannerLevel = level;
        _lastBannerTs    = now;
      }
    } else {
      // urgent/attention: always show, but update state
      banner = priorityBanner;
      markSeen(bannerKey);
      _lastBannerLevel = level;
      _lastBannerTs    = now;
    }
  }

  /* ── Chips: max 3, deduplicate by action+workflowId ────────── */
  const seen    = new Set();
  const chips   = [];
  for (const chip of nextActionItems) {
    if (chips.length >= 3) { suppressed.push("chip-limit"); break; }
    const chipKey = `${chip.action}:${chip.workflowId ?? chip.workflowType ?? "generic"}`;
    if (seen.has(chipKey)) { suppressed.push("chip-dup"); continue; }
    seen.add(chipKey);
    chips.push(chip);
  }

  /* ── Insight text: suppress if it says the same thing as banner ─ */
  let finalInsight = null;
  if (insightText) {
    // Simple overlap check: if banner already mentions the same entity/workflow, skip
    const bannerText    = banner?.text ?? "";
    const insightCopy   = insightText.toLowerCase();
    const bannerWords   = bannerText.toLowerCase().split(/\s+/).filter((w) => w.length > 5);
    const hasOverlap    = bannerWords.length > 0 && bannerWords.some((w) => insightCopy.includes(w));

    if (hasOverlap && banner) {
      suppressed.push("insight-overlaps-banner");
    } else if (!isCooledDown(`insight:${insightText.slice(0, 40)}`)) {
      suppressed.push("insight-cooldown");
    } else {
      finalInsight = insightText;
      markSeen(`insight:${insightText.slice(0, 40)}`);
    }
  }

  /* ── Followup panel: only show when there's content ────────── */
  const shouldShowFollowup = followupItems.length > 0;

  return { banner, chips, insightText: finalInsight, shouldShowFollowup, suppressed };
}

/** Clear session state (call on sign-out or conversation reset). */
export function clearRenderState() {
  _hintCooldowns.clear();
  _lastBannerLevel = null;
  _lastBannerTs    = 0;
}

/** Dev inspection — suppression state snapshot. */
export function getRenderPressureSnapshot() {
  return {
    hintCooldowns:   Object.fromEntries(_hintCooldowns),
    lastBannerLevel: _lastBannerLevel,
    lastBannerTs:    _lastBannerTs,
    activeCooldowns: [..._hintCooldowns.entries()]
      .filter(([, ts]) => Date.now() - ts < HINT_COOLDOWN_MS)
      .map(([k]) => k),
  };
}
